import React, { useState, useEffect } from "react";
import {
  Users as UsersIcon, KeyRound, Activity, LogOut, ArrowLeft, Bot, Cpu, Plug, History, GraduationCap, AlertTriangle,
  Boxes, Settings, Menu, SlidersHorizontal,
} from "lucide-react";

import { Alert, Avatar, Button, EmptyState, Modal, Page, SideNav, useConfirm, useToast } from "../../Ui/index.jsx";
import { apiGet, apiPost, returnToOwnCompany } from "../../Api/Client.js";
import { ATGLogo } from "../../Components/icons/icons.jsx";
import { McpScreen } from "../agents/McpScreen.jsx";
import { CompanySettingsCard, ModulesAndAddOns } from "./CompanyAdminCards.jsx";
import { AlumniRecordsView } from "../../Products/people/index.js";
import { PayRulesView } from "../../Products/payroll/index.js";
import { AccessActivityTab } from "./tabs/AccessActivityTab.jsx";
import { AccessDrawer } from "./tabs/AccessDrawer.jsx";
import { AccessModelTab } from "./tabs/AccessModelTab.jsx";
import { AiUsageTab } from "./tabs/AiUsageTab.jsx";
import { DefaultersTab } from "./tabs/DefaultersTab.jsx";
import { IntegrationsHealthTab } from "./tabs/IntegrationsHealthTab.jsx";
import { LoginActivityTab } from "./tabs/LoginActivityTab.jsx";
import { PeopleTab } from "./tabs/PeopleTab.jsx";
import "../../Styles/AdminPanel.css";

// The company admin panel (Entry 20, design A), at /company-admin/: one company only. Sections marked
// operatorOnly are platform-wide figures and show only to the platform operator. The panel is its own
// full-screen layout (a section list beside one tab); each tab is a component of its own in ./tabs.
const SECTIONS = [
  { key: "users", label: "People", icon: UsersIcon },
  { key: "modules", label: "Modules and add-ons", icon: Boxes },
  { key: "settings", label: "Company settings", icon: Settings },
  { key: "access", label: "Access model", icon: KeyRound },
  { key: "integrations", label: "Integrations", icon: Plug },
  { key: "audit", label: "Login activity", icon: Activity },
  { key: "access_activity", label: "Access activity", icon: History },
  { key: "defaulters", label: "Defaulters", icon: AlertTriangle },
  { key: "aiusage", label: "AI Usage", icon: Cpu },
  { key: "mcp", label: "MCP agents", icon: Bot, addon: "axon" },
  { key: "alumni", label: "Alumni Records", icon: GraduationCap, addon: "people.alumni" },
  { key: "payrules", label: "Pay rules", icon: SlidersHorizontal, addon: "payroll.formulas" },
];

// What the People tab asks the server for. The server filters, sorts and pages (issue 163).
const PEOPLE_PAGE_SIZE = 50;
const FIRST_PEOPLE_QUERY = {
  q: "", status: "active", type: "all", department: "all",
  sort: { k: "name", dir: "asc" }, page: 1, pageSize: PEOPLE_PAGE_SIZE,
};
const peopleUrl = (qy) => {
  const p = new URLSearchParams();
  p.set("page", String(qy.page));
  p.set("page_size", String(qy.pageSize));
  p.set("status", qy.status);
  p.set("type", qy.type);
  if (qy.department && qy.department !== "all") p.set("department", qy.department);
  if (qy.q) p.set("q", qy.q);
  p.set("sort", (qy.sort.dir === "desc" ? "-" : "") + qy.sort.k);
  return `/Users/admin-panel/users/?${p.toString()}`;
};

export function AdminPanelScreen({ data, navigate, logout, reload }) {
  const isOperator = !!data?.me?.user?.isOperator;
  const [addonsOn, setAddonsOn] = useState(null);
  useEffect(() => {
    apiGet("/EnterpriseCore/plugins/")
      .then((res) => setAddonsOn(new Set((res?.plugins || []).filter((p) => p.enabled).map((p) => p.code))))
      .catch(() => setAddonsOn(new Set()));
  }, []);
  const sections = SECTIONS.filter((s) => {
    if (s.operatorOnly && !isOperator) return false;
    if (s.addon && (!addonsOn || !addonsOn.has(s.addon))) return false;
    return true;
  });
  const [section, setSectionState] = useState(() => {
    const asked = new URLSearchParams(window.location.search).get("section");
    return sections.some((s) => s.key === asked) ? asked : "users";
  });
  // On a phone or narrow window the section list is a sheet that opens over the page; this is its open state.
  const [navOpen, setNavOpen] = useState(false);
  const setSection = (key) => {
    setSectionState(key);
    setNavOpen(false);
    try { window.history.replaceState(null, "", key === "users" ? "/company-admin/" : `/company-admin/?section=${key}`); } catch (e) {}
  };
  const support = data?.me?.support || null;
  const leaveSupport = async () => {
    // If the visit could not be closed it is still open on the server: say so and stay, rather than
    // leaving the operator believing they are out.
    try {
      await apiPost(`/EnterpriseCore/platform/support/${support.id}/close/`, {});
    } catch (e) {
      setErr(e?.payload?.detail || "Could not leave this company. Please try again.");
      return;
    }
    // Entry 20 review: back to the operator's own company, not the "1" default a removed id falls to.
    try { returnToOwnCompany(data?.me?.user?.ownCompanyId); } catch (e) {}
    window.location.assign("/platform/");
  };
  // The People tab shows ONE PAGE of the company's people; search, filters, sort and paging are
  // done by the server, so the tab opens as fast for 5,000 people as for 50 (issue 163).
  const [people, setPeople] = useState({ users: [], count: 0, page: 1, pages: 1, departments: [] });
  const [peopleQuery, setPeopleQuery] = useState(FIRST_PEOPLE_QUERY);
  const [peopleBusy, setPeopleBusy] = useState(true);
  const peopleSeq = React.useRef(0);
  const [matrix, setMatrix] = useState(null);
  // null = not loaded yet (shows "Loading…"), so an empty list always means the server said "none".
  const [audit, setAudit] = useState(null);
  const [accessActivity, setAccessActivity] = useState(null);
  const [defaulters, setDefaulters] = useState(null);
  const [defaultersDays, setDefaultersDays] = useState(30);
  const [health, setHealth] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [aiDays, setAiDays] = useState(14);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [accessUser, setAccessUser] = useState(null);
  const toast = useToast();
  const confirm = useConfirm();

  const me = data?.me?.user || {};
  const meName = me.fullName || me.full_name || me.username || "Admin";

  const load = async () => {
    setLoading(true); setErr("");
    try {
      setMatrix(await apiGet("/Users/admin-panel/matrix/"));
    } catch (e) { setErr(e?.payload?.detail || e?.message || "Only your company's admins can open this panel."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Fetch the page the query describes. Answers can arrive out of order while someone is typing or
  // clicking, so only the latest request is allowed to land.
  const loadPeople = async (query) => {
    const mine = ++peopleSeq.current;
    setPeopleBusy(true);
    try {
      const r = await apiGet(peopleUrl(query));
      if (mine === peopleSeq.current) setPeople(r);
    } catch (e) {
      if (mine === peopleSeq.current) setErr(e?.payload?.detail || e?.message || "Only your company's admins can open this panel.");
    }
    if (mine === peopleSeq.current) setPeopleBusy(false);
  };
  useEffect(() => { loadPeople(peopleQuery); }, [peopleQuery]);
  // A failed load is shown as a failure with a retry, never as an empty result: "Nothing yet" or
  // "No defaults" must only appear when the server really answered with nothing.
  const [tabError, setTabError] = useState({});
  const [tabRetry, setTabRetry] = useState(0);
  const loadTab = (key, url, onData) => {
    setTabError((cur) => ({ ...cur, [key]: "" }));
    return apiGet(url).then(onData).catch((e) => {
      setTabError((cur) => ({ ...cur, [key]: e?.payload?.detail || "This could not be loaded right now." }));
    });
  };
  useEffect(() => {
    if (section === "audit" && audit === null)
      loadTab("audit", "/Users/admin-panel/login-audit/", (a) => setAudit(a.logins || []));
  }, [section, tabRetry]);
  useEffect(() => {
    if (section === "access_activity" && accessActivity === null)
      loadTab("access_activity", "/Users/admin-panel/access-activity/", (a) => setAccessActivity(a.events || []));
  }, [section, tabRetry]);
  useEffect(() => {
    if (section !== "defaulters") return;
    setDefaulters(null);
    loadTab("defaulters", `/Users/admin-panel/defaulters/?days=${defaultersDays}`, setDefaulters);
  }, [section, defaultersDays, tabRetry]);
  // Fetched on mount, not gated to the Integrations section: the section list itself needs to know whether
  // anything is down (it shows that item in red), so this cannot wait for a click into that section.
  // The backend already caches this response for 45s, so loading it unconditionally costs nothing extra.
  useEffect(() => {
    loadTab("integrations", "/Users/admin-panel/integrations-health/", setHealth);
  }, [tabRetry]);
  useEffect(() => {
    if (section === "aiusage")
      loadTab("aiusage", `/Users/admin-panel/ai-usage/?days=${aiDays}`, setAiUsage);
  }, [section, aiDays, tabRetry]);

  // The server returns the person's freshly resolved access, and ALL of it has
  // to land back in state. The access grid is rendered from page_access and
  // page_exceptions; if those are not copied across, ticking a box writes the
  // grant on the server and changes nothing on screen — which reads as "the
  // click did nothing", and clicking again silently toggles it back off.
  const applyActionResult = (target, r) => ({
    ...target,
    is_active: r.is_active ?? target.is_active,
    is_superuser: r.is_superuser ?? target.is_superuser,
    overrides: r.overrides ?? target.overrides,
    page_access: r.page_access ?? target.page_access,
    page_overrides: r.page_overrides ?? target.page_overrides,
  });

  const act = async (id, body) => {
    try {
      const r = await apiPost(`/Users/admin-panel/users/${id}/action/`, body);
      setPeople((prev) => ({ ...prev, users: prev.users.map((u) => (u.id === id ? applyActionResult(u, r) : u)) }));
      setAccessUser((a) => (a && a.id === id ? applyActionResult(a, r) : a));
      // Switching someone off (or making them an operator) can take them out of the filtered view
      // or move them in the sort, which only the server can tell: ask it for the page again.
      if (body.action === "set_active" || body.action === "set_superuser") loadPeople(peopleQuery);
    } catch (e) { toast(e?.payload?.detail || e?.message || "That change did not go through. Please try again."); }
  };

  // The drawer opens at once on the row's page grid; the long parts (hand-set exceptions) come
  // from one person's detail, and the grid stays locked until they have arrived.
  const openAccess = async (u) => {
    setAccessUser({ ...u, detailLoading: true });
    try {
      const d = await apiGet(`/Users/admin-panel/users/${u.id}/`);
      setAccessUser((a) => (a && a.id === u.id ? { ...a, ...d, detailLoading: false } : a));
    } catch (e) {
      const why = e?.payload?.detail || e?.message || "Could not load this person's access.";
      setAccessUser((a) => (a && a.id === u.id ? { ...a, detailLoading: false, detailError: why } : a));
    }
  };

  const impersonate = (u) => {
    if (u.is_superuser) { toast("You can't log in as another platform operator.", "info"); return; }
    confirm.ask({
      title: `Log in as ${u.name}?`,
      message: `You will see the whole app exactly as they do: their menu, what they can open, and their emails and phone numbers hidden. Use "Exit" in the top bar to come back to yourself.`,
      confirmLabel: "Log in as them",
      onConfirm: () => loginAs(u),
    });
  };

  const loginAs = async (u) => {
    try {
      await apiPost(`/Users/admin-panel/users/${u.id}/impersonate/`, {});
      // Remembered so "Exit — back to admin" (App.jsx) returns here instead
      // of defaulting to /home/ — that was landing the admin on the target
      // user's own homepage, not back on this People list.
      try { sessionStorage.setItem("atg-impersonate-return-to", "/company-admin/"); } catch (e) {}
      window.location.href = "/home/"; // full reboot as the target user
    } catch (e) { toast(e?.payload?.detail || e?.message || "Could not log in as that person."); }
  };

  const navItems = sections.map((s) => ({
    key: s.key, label: s.label, icon: <s.icon size={16} aria-hidden="true" />,
    tone: s.key === "integrations" && health?.any_down ? "danger" : undefined,
  }));
  const sectionNav = <SideNav label="Admin sections" items={navItems} value={section} onChange={setSection} />;
  const sectionFoot = (
    <>
      <Button variant="ghost" leftIcon={<ArrowLeft size={14} aria-hidden="true" />} onClick={() => navigate("/home/")}>Exit admin</Button>
      <div className="Adm-Me">
        <Avatar name={meName} size="sm" />
        <span className="Adm-Me__name">{meName}</span>
        <Button variant="ghost" className="ds-btn--icon" onClick={logout} title="Log out" aria-label="Log out"><LogOut size={14} aria-hidden="true" /></Button>
      </div>
    </>
  );

  return (
    <div className="Adm">
      <aside className="Adm-Nav">
        <div className="Adm-Brand"><ATGLogo company={data?.me?.company} /><span>{data?.me?.activeTenant?.name || "Company"} admin</span></div>
        <div className="Adm-NavList">{sectionNav}</div>
        <div className="Adm-NavFoot">{sectionFoot}</div>
      </aside>

      {/* Below 860px the side list is not on screen: the button below opens the same list as a sheet. */}
      <Modal side size="sm" title="Admin sections" open={navOpen} onClose={() => setNavOpen(false)} closeOnBackdrop>
        <div className="Adm-Sheet">{sectionNav}<div className="Adm-NavFoot">{sectionFoot}</div></div>
      </Modal>

      <main className="Adm-Main">
        <div className="Adm-MobileBar">
          <Button onClick={() => setNavOpen(true)} aria-label="Open the section menu" aria-expanded={navOpen}>
            <Menu size={16} aria-hidden="true" /> {sections.find((x) => x.key === section)?.label}
          </Button>
        </div>
        {support && (
          <Alert tone="warning" className="Adm-Notice">
            <span>You are inside {data?.me?.activeTenant?.name} as platform support until {new Date(support.endsAt).toLocaleTimeString()}. Changes you make here are recorded in their trail.</span>
            <Button size="sm" onClick={leaveSupport}>Leave this company</Button>
          </Alert>
        )}
        {err && <Alert tone="danger" className="Adm-Notice">{err}</Alert>}
        {loading ? <Page><EmptyState>Loading…</EmptyState></Page> : tabError[section] ? (
          <Page>
            <Alert tone="danger" className="Adm-Notice is-inline">
              <span>{tabError[section]}</span>
              <Button size="sm" onClick={() => setTabRetry((n) => n + 1)}>Try again</Button>
            </Alert>
          </Page>
        ) : (
          <>
            {section === "users" && <PeopleTab people={people} query={peopleQuery} setQuery={setPeopleQuery} busy={peopleBusy} act={act} onAccess={openAccess} onImpersonate={impersonate} activeDepartments={data.activeDepartments} isOperator={isOperator} />}
            {section === "modules" && <ModulesAndAddOns onPluginsChanged={() => reload && reload(["me"])} />}
            {section === "settings" && <CompanySettingsCard onChanged={() => reload && reload(["me"])} />}
            {section === "access" && <AccessModelTab matrix={matrix} />}
            {section === "integrations" && <IntegrationsHealthTab health={health} />}
            {section === "audit" && <LoginActivityTab audit={audit} />}
            {section === "access_activity" && <AccessActivityTab events={accessActivity} />}
            {section === "defaulters" && <DefaultersTab report={defaulters} days={defaultersDays} setDays={setDefaultersDays} />}
            {section === "aiusage" && <AiUsageTab usage={aiUsage} days={aiDays} setDays={setAiDays} />}
            {section === "mcp" && <McpScreen data={data} reload={reload} />}
            {section === "alumni" && <AlumniRecordsView />}
            {section === "payrules" && <PayRulesView />}
          </>
        )}
      </main>

      {accessUser && (
        <AccessDrawer user={accessUser} matrix={matrix}
          onClose={() => setAccessUser(null)} act={act} />
      )}
      {confirm.dialog}
    </div>
  );
}
